import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";
import { useMutation, useQuery, useLazyQuery } from "@apollo/client";
import {
  TomConversation,
  TomMessage,
  TomActionResult,
  TomPendingActionStatus,
} from "../../generated/graphql";
import {
  CREATE_TOM_CONVERSATION,
  SEND_TOM_MESSAGE,
  CONFIRM_TOM_ACTION,
  REJECT_TOM_ACTION,
  RATE_TOM_MESSAGE,
  GET_TOM_CONVERSATIONS,
  GET_TOM_CONVERSATION,
  DELETE_TOM_CONVERSATION,
} from "./queries";
import { useTomContext, TomPageContext } from "./useTomContext";

export interface PageData {
  entityType: string;
  entityId: string;
  summary: string;
  data: Record<string, unknown>;
}

interface TomContextValue {
  isOpen: boolean;
  togglePanel: () => void;
  openPanel: () => void;
  closePanel: () => void;

  // Conversation state
  activeConversation: TomConversation | null;
  messages: TomMessage[];
  conversations: TomConversation[];
  loading: boolean;
  sending: boolean;

  // Actions
  sendMessage: (content: string, imageUrls?: string[]) => Promise<void>;
  confirmAction: (actionId: string) => Promise<TomActionResult | null>;
  rejectAction: (actionId: string, reason?: string) => Promise<boolean>;
  rateMessage: (messageId: string, feedback: "HELPFUL" | "NOT_HELPFUL") => Promise<void>;
  startNewConversation: () => Promise<void>;
  switchConversation: (id: string) => void;
  deleteConversation: (id: string) => Promise<boolean>;

  // Page context
  pageContext: TomPageContext;
  pageData: PageData | null;
  registerPageData: (data: PageData | null) => void;
}

const TomContext = createContext<TomContextValue | null>(null);

export const useTom = (): TomContextValue => {
  const ctx = useContext(TomContext);
  if (!ctx) throw new Error("useTom must be used within TomProvider");
  return ctx;
};

const STORAGE_KEY_CONV = "tom_active_conversation";
const STORAGE_KEY_OPEN = "tom_panel_open";

export const TomProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(() => localStorage.getItem(STORAGE_KEY_OPEN) === "true");
  const [activeConversation, setActiveConversation] = useState<TomConversation | null>(null);
  const [messages, setMessages] = useState<TomMessage[]>([]);
  const [pageData, setPageData] = useState<PageData | null>(null);
  const [hasRestoredConversation, setHasRestoredConversation] = useState(false);

  const pageContext = useTomContext();

  // Queries
  const { data: conversationsData, refetch: refetchConversations } = useQuery(GET_TOM_CONVERSATIONS, {
    skip: !isOpen,
    fetchPolicy: "network-only",
  });
  const conversations = (conversationsData?.tomConversations || []) as TomConversation[];

  // Lazy query to restore a conversation on mount
  const [loadConversation] = useLazyQuery(GET_TOM_CONVERSATION, { fetchPolicy: "network-only" });

  // Mutations
  const [createConversation] = useMutation(CREATE_TOM_CONVERSATION);
  const [sendTomMessage, { loading: sending }] = useMutation(SEND_TOM_MESSAGE);
  const [confirmTomAction] = useMutation(CONFIRM_TOM_ACTION);
  const [rejectTomAction] = useMutation(REJECT_TOM_ACTION);
  const [rateTomMessage] = useMutation(RATE_TOM_MESSAGE);
  const [deleteTomConversation] = useMutation(DELETE_TOM_CONVERSATION);

  // Persist panel open state
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_OPEN, isOpen ? "true" : "false");
  }, [isOpen]);

  // Persist active conversation ID
  useEffect(() => {
    if (activeConversation) {
      localStorage.setItem(STORAGE_KEY_CONV, activeConversation.id);
    }
  }, [activeConversation]);

  // Restore conversation on mount
  useEffect(() => {
    if (hasRestoredConversation) return;
    setHasRestoredConversation(true);

    const savedId = localStorage.getItem(STORAGE_KEY_CONV);
    if (!savedId) return;

    loadConversation({ variables: { id: savedId } }).then(({ data }) => {
      const conv = data?.tomConversation;
      if (conv) {
        setActiveConversation(conv.conversation as TomConversation);
        setMessages((conv.messages || []) as TomMessage[]);
      }
    }).catch(() => {
      // Conversation may have been deleted — clear stale reference
      localStorage.removeItem(STORAGE_KEY_CONV);
    });
  }, [hasRestoredConversation, loadConversation]);

  const togglePanel = useCallback(() => setIsOpen(prev => !prev), []);
  const openPanel = useCallback(() => setIsOpen(true), []);
  const closePanel = useCallback(() => setIsOpen(false), []);

  const registerPageData = useCallback((data: PageData | null) => {
    setPageData(data);
  }, []);

  const ensureConversation = useCallback(async (): Promise<string> => {
    if (activeConversation) return activeConversation.id;

    const { data } = await createConversation({
      variables: {
        input: {
          moduleContext: pageContext.module,
          entityContext: pageContext.entityId,
          title: `Tom - ${pageContext.module}`,
        },
      },
    });

    const conv = data?.createTomConversation as TomConversation;
    setActiveConversation(conv);
    refetchConversations();
    return conv.id;
  }, [activeConversation, pageContext, createConversation, refetchConversations]);

  const sendMessage = useCallback(async (content: string, imageUrls?: string[]) => {
    const conversationId = await ensureConversation();

    // Optimistically add user message
    const userMsg = {
      id: `temp-${Date.now()}`,
      conversationId,
      role: "USER" as TomMessage["role"],
      content,
      imageUrls: imageUrls || [],
      createdAt: new Date().toISOString(),
    } as TomMessage & { imageUrls?: string[] };
    setMessages(prev => [...prev, userMsg]);

    try {
      const { data } = await sendTomMessage({
        variables: {
          input: {
            conversationId,
            content,
            imageUrls: imageUrls && imageUrls.length > 0 ? imageUrls : undefined,
            pageContext: {
              module: pageContext.module,
              path: pageContext.path,
              entityId: pageContext.entityId,
              entityType: pageContext.entityType,
              pageDataSummary: pageData?.summary,
            },
          },
        },
      });

      const assistantMsg = data?.sendTomMessage?.message as TomMessage;
      if (assistantMsg) {
        setMessages(prev => [...prev, assistantMsg]);
      }
    } catch (err) {
      // Add error message
      const errorMsg: TomMessage = {
        id: `error-${Date.now()}`,
        conversationId,
        role: "SYSTEM" as TomMessage["role"],
        content: `Error: ${(err as Error).message}`,
        createdAt: new Date().toISOString(),
      } as TomMessage;
      setMessages(prev => [...prev, errorMsg]);
    }
  }, [ensureConversation, sendTomMessage, pageContext, pageData]);

  const confirmAction = useCallback(async (actionId: string): Promise<TomActionResult | null> => {
    if (!activeConversation) return null;
    try {
      const { data } = await confirmTomAction({
        variables: { conversationId: activeConversation.id, actionId },
      });
      const result = data?.confirmTomAction as TomActionResult;

      // Update the action status in messages
      setMessages(prev => prev.map(msg => ({
        ...msg,
        pendingActions: msg.pendingActions?.map(a =>
          a.id === actionId
            ? { ...a, status: result.status === "EXECUTED" ? TomPendingActionStatus.Executed : TomPendingActionStatus.Failed, resultId: result.resultId, resultSummary: result.resultSummary, error: result.error }
            : a
        ),
      })));

      // Auto-follow-up: tell Tom the action succeeded so it proceeds with next steps
      if (result.status === "EXECUTED") {
        const followUp = `Action confirmed and executed: ${result.resultSummary || "done"}${result.resultId ? ` (ID: ${result.resultId})` : ""}. Please continue with any remaining steps.`;

        // Send follow-up as a background message (don't show it as user typed)
        try {
          const { data: followUpData } = await sendTomMessage({
            variables: {
              input: {
                conversationId: activeConversation.id,
                content: followUp,
                pageContext: {
                  module: pageContext.module,
                  path: pageContext.path,
                  entityId: pageContext.entityId,
                  entityType: pageContext.entityType,
                  pageDataSummary: pageData?.summary,
                },
              },
            },
          });

          const nextMsg = followUpData?.sendTomMessage?.message as TomMessage;
          if (nextMsg) {
            setMessages(prev => [...prev, nextMsg]);
          }
        } catch {
          // Follow-up failed silently — not critical
        }
      }

      return result;
    } catch (err) {
      return { actionId, status: "FAILED", error: (err as Error).message } as TomActionResult;
    }
  }, [activeConversation, confirmTomAction, sendTomMessage, pageContext, pageData]);

  const rejectAction = useCallback(async (actionId: string, reason?: string): Promise<boolean> => {
    if (!activeConversation) return false;
    try {
      const { data } = await rejectTomAction({
        variables: { conversationId: activeConversation.id, actionId, reason },
      });

      setMessages(prev => prev.map(msg => ({
        ...msg,
        pendingActions: msg.pendingActions?.map(a =>
          a.id === actionId ? { ...a, status: TomPendingActionStatus.Rejected } : a
        ),
      })));

      return data?.rejectTomAction ?? false;
    } catch {
      return false;
    }
  }, [activeConversation, rejectTomAction]);

  const rateMessage = useCallback(async (messageId: string, feedback: "HELPFUL" | "NOT_HELPFUL") => {
    await rateTomMessage({ variables: { messageId, feedback } });
  }, [rateTomMessage]);

  const startNewConversation = useCallback(async () => {
    setActiveConversation(null);
    setMessages([]);
    localStorage.removeItem(STORAGE_KEY_CONV);
  }, []);

  const switchConversation = useCallback((id: string) => {
    const conv = conversations.find(c => c.id === id);
    if (conv) {
      setActiveConversation(conv);
      setMessages([]); // Will be loaded by the panel
    }
  }, [conversations]);

  const deleteConversation = useCallback(async (id: string): Promise<boolean> => {
    try {
      await deleteTomConversation({ variables: { id } });
      // If we deleted the active conversation, clear it
      if (activeConversation?.id === id) {
        setActiveConversation(null);
        setMessages([]);
        localStorage.removeItem(STORAGE_KEY_CONV);
      }
      refetchConversations();
      return true;
    } catch {
      return false;
    }
  }, [activeConversation, deleteTomConversation, refetchConversations]);

  return (
    <TomContext.Provider
      value={{
        isOpen,
        togglePanel,
        openPanel,
        closePanel,
        activeConversation,
        messages,
        conversations,
        loading: false,
        sending,
        sendMessage,
        confirmAction,
        rejectAction,
        rateMessage,
        startNewConversation,
        switchConversation,
        deleteConversation,
        pageContext,
        pageData,
        registerPageData,
      }}
    >
      {children}
    </TomContext.Provider>
  );
};
